import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, CheckCheck } from "lucide-react";
import { api, fmtDateTime } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

function urlB64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

async function enablePush() {
  try {
    if (!("serviceWorker" in navigator) || !("PushManager" in window) || !("Notification" in window))
      return;
    const reg = await navigator.serviceWorker.register("/sw.js");
    if (Notification.permission === "denied") return;
    let perm = Notification.permission;
    if (perm === "default") perm = await Notification.requestPermission();
    if (perm !== "granted") return;
    const { data } = await api.get("/push/vapid-public-key");
    if (!data.public_key) return;
    const existing = await reg.pushManager.getSubscription();
    const sub =
      existing ||
      (await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlB64ToUint8Array(data.public_key),
      }));
    await api.post("/push/subscribe", { subscription: sub.toJSON() });
  } catch (e) {
    /* push is best-effort */
  }
}

export const NotificationBell = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const pushed = useRef(false);

  const load = useCallback(() => {
    api.get("/notifications").then(({ data }) => {
      setItems(data.notifications || []);
      setUnread(data.unread || 0);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    window.addEventListener("notifications:refresh", load);
    if (!pushed.current) {
      pushed.current = true;
      enablePush();
    }
    return () => {
      clearInterval(t);
      window.removeEventListener("notifications:refresh", load);
    };
  }, [load]);

  const openItem = async (n) => {
    setOpen(false);
    if (n.status === "unread") {
      await api.post(`/notifications/${n.id}/read`).catch(() => {});
      load();
    }
    if (n.link) navigate(n.link);
  };

  const markAll = async () => {
    await api.post("/notifications/read-all").catch(() => {});
    load();
  };

  return (
    <Popover open={open} onOpenChange={(v) => { setOpen(v); if (v) load(); }}>
      <PopoverTrigger asChild>
        <button
          data-testid="notification-bell"
          className="relative flex h-11 w-11 items-center justify-center rounded-md text-brand hover:bg-brand-tint/60"
          aria-label="Notifications"
        >
          <Bell className="h-5 w-5" />
          {unread > 0 && (
            <span
              data-testid="notification-badge"
              className="absolute right-1.5 top-1.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-crimson-500 px-1 text-[10px] font-bold text-white"
            >
              {unread > 99 ? "99+" : unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[360px] p-0" data-testid="notification-panel">
        <div className="flex items-center justify-between border-b border-brand-line px-4 py-3">
          <p className="font-display text-sm font-semibold text-brand-ink">Notifications</p>
          {unread > 0 && (
            <Button variant="ghost" size="sm" className="h-7 text-xs" data-testid="notif-mark-all"
              onClick={markAll}>
              <CheckCheck className="mr-1 h-3.5 w-3.5" /> Mark all read
            </Button>
          )}
        </div>
        <div className="max-h-[420px] overflow-y-auto">
          {items.length === 0 && (
            <p className="px-4 py-10 text-center text-sm text-slate-500" data-testid="notif-empty">
              You're all caught up.
            </p>
          )}
          {items.map((n) => (
            <button
              key={n.id}
              data-testid={`notif-item-${n.id}`}
              onClick={() => openItem(n)}
              className={`block w-full border-b border-brand-line/60 px-4 py-3 text-left transition-colors hover:bg-brand-tint/50 ${
                n.status === "unread" ? "bg-brand-tint/30" : ""
              }`}
            >
              <div className="flex items-start gap-2">
                {n.status === "unread" && (
                  <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-crimson-500" />
                )}
                <div className="min-w-0">
                  <p className="text-sm font-medium text-brand-ink">{n.title}</p>
                  <p className="mt-0.5 text-xs text-slate-600">{n.body}</p>
                  <p className="mt-1 text-[11px] text-slate-400">{fmtDateTime(n.created_at)}</p>
                </div>
              </div>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};
